import BaseSelect from 'components/Selects/BaseSelect/BaseSelect';
import { findSelectOption } from 'components/Selects/BaseSelect/utils/baseSelect.utils';
import { Button, Form } from 'react-bootstrap';
import {
  getIpawsAreaFindings,
  getIpawsAreaMetrics,
  getIpawsAreaShapes,
} from '../../../../utils/alertCreator.utils';
import {
  authorizedAreasFailureReason,
  authorizedJurisdictionFor,
  draftSameCode,
  isAuthorizedSameCode,
  jurisdictionOptionLabel,
  unauthorizedGeocodeFinding,
} from '../../../../utils/femaJurisdictions.utils';
import {
  getIpawsAreaShapeLabel,
  getIpawsSameGeocodeCount,
  hasIpawsSameGeocode,
  IPAWS_AREA_LIMITS,
  IPAWS_AREA_TYPES,
  IPAWS_LEGACY_SAME_GEOCODE_LIMIT,
} from '../../../../utils/ipawsArea.utils';
import {
  formatChannelNames,
  getChannelsMissingForArea,
} from '../../../../utils/ipawsChannelRules.utils';
import IpawsFieldFindings, {
  getIpawsFieldFindings,
  getIpawsFindingElementId,
} from '../IpawsFieldFindings/IpawsFieldFindings';
import IpawsTextField from '../IpawsTextField/IpawsTextField';

/**
 * Summarizes every map shape in the IPAWS draft and shows area-related problems.
 * This is a read-only summary; shapes are added and edited with the map tools panel.
 */
const IpawsAffectedAreaSection = ({
  draft,
  findings,
  permittedArea,
  hasReviewedDraft,
  validationIssues,
  onTextChange,
  onReviewDraft,
  onClearJurisdiction,
  authorizedAreas = {},
  onSelectAuthorizedJurisdiction,
}) => {
  const areaFindings = getIpawsAreaFindings(draft, { permittedArea });

  const areaShapes = getIpawsAreaShapes(draft);
  const hasCircle = areaShapes.some((shape) => shape.type === IPAWS_AREA_TYPES.CIRCLE);
  const isNwemSelected = draft.deliveryChannels?.includes('NWEM');
  const isWeaSelected = draft.deliveryChannels?.includes('CMAS');
  const geocodeFindings = getIpawsFieldFindings('geocodes', findings);
  const sameGeocodeCount = getIpawsSameGeocodeCount(draft);

  // The areas the COG may alert (card BE-13). The draft's code selects its own entry; a code
  // from the search that the COG does not hold is said so here, before Review.
  const authorizedJurisdictions = Array.isArray(authorizedAreas.jurisdictions)
    ? authorizedAreas.jurisdictions
    : [];
  const currentSameCode = draftSameCode(draft);
  const selectedAuthorizedCode = isAuthorizedSameCode(authorizedJurisdictions, currentSameCode)
    ? currentSameCode
    : '';
  const authorizedAreaOptions = authorizedJurisdictions.map((jurisdiction) => ({
    value: jurisdiction.sameCode,
    label: jurisdictionOptionLabel(jurisdiction),
  }));
  const unauthorizedNote = unauthorizedGeocodeFinding(draft, authorizedJurisdictions);
  // Per-(channel, geocode) scoping (card FE-06): the selected channels the targeted area lacks.
  const missingChannels = getChannelsMissingForArea(
    authorizedJurisdictionFor(authorizedJurisdictions, currentSameCode),
    draft.deliveryChannels,
  );
  const authorizedStatus = authorizedAreas.isLoading
    ? 'Asking the FEMA service which areas this COG may alert…'
    : authorizedAreas.isError
      ? `The authorized areas could not be read: ${authorizedAreasFailureReason(authorizedAreas.error)}`
      : authorizedJurisdictions.length
        ? `${authorizedJurisdictions.length} area${
            authorizedJurisdictions.length === 1 ? '' : 's'
          } authorized area${
            authorizedJurisdictions.length === 1 ? '' : 's'
          }; each lists the channels FEMA authorized.${
            authorizedAreas.tenantNote ? ` ${authorizedAreas.tenantNote}` : ''
          }`
        : 'The issuing authority has no authorized alerting areas.';

  // The sent polygon repeats its first point at the end, so that closing point is counted here.
  const { shapeCount, pointCount } = getIpawsAreaMetrics(draft);

  const formatDistance = (value) =>
    Number.isFinite(Number(value)) ? `${Number(value).toLocaleString()} km` : null;

  const getShapeDescription = (shape) => {
    if (shape.type === IPAWS_AREA_TYPES.CIRCLE) {
      const radius = formatDistance(shape.radiusKm);
      return radius ? `${radius} radius` : 'Radius not set';
    }

    if (shape.type === IPAWS_AREA_TYPES.SQUARE) {
      const sideLength = formatDistance(shape.sideLengthKm);
      return sideLength ? `${sideLength} sides` : 'Side length not set';
    }

    return `${shape.coordinates?.length ?? 0} drawn vertices`;
  };

  return (
    <fieldset className="alert-creator-section alert-creator-section-ipaws border rounded-3 p-3 shadow-sm">
      <legend className="alert-creator-section-title d-flex align-items-center">
        Affected area
      </legend>

      <IpawsTextField
        controlId="ipaws-alert-area-description"
        fieldPath="areaDesc"
        label="Affected area description"
        value={draft.areaDesc}
        onChange={onTextChange}
        findings={findings}
        placeholder="Describe the affected area in familiar terms"
        required
      />

      <div
        className="alert-creator-readonly-info"
        aria-invalid={geocodeFindings.length > 0}
        aria-describedby={geocodeFindings.length ? getIpawsFindingElementId('geocodes') : undefined}
      >
        <strong className="d-block">Target County (SAME Geocode)</strong>
        <small className="d-block mb-2" aria-live="polite">
          Resolved SAME geocodes: {sameGeocodeCount}/{IPAWS_LEGACY_SAME_GEOCODE_LIMIT}
        </small>

        <Form.Group controlId="ipaws-authorized-area" className="mb-2">
          <Form.Label className="mb-1 small">
            Authorized Alerting Areas (Issuing Authority:{' '}
            {authorizedAreas.organizationName || 'Not provided'})
          </Form.Label>
          <small className="d-block mb-2">
            These are the geographic areas this alerting authority is authorized to alert through
            FEMA IPAWS. Available delivery channels are shown for each area based on FEMA
            authorization.
          </small>
          <BaseSelect
            inputId="ipaws-authorized-area"
            isSearchable
            // The section is dark: the theme colours come from the same classes the other controls use.
            classNames={{
              control: () => 'alert-creator-control alert-creator-select-control',
            }}
            selectProps={{
              options: authorizedAreaOptions,
              value: findSelectOption(authorizedAreaOptions, selectedAuthorizedCode),
              placeholder: 'Select an authorized alerting area…',
              isDisabled: !authorizedJurisdictions.length,
              onChange: (option) => {
                const jurisdiction = authorizedJurisdictionFor(
                  authorizedJurisdictions,
                  option?.value ?? '',
                );
                if (jurisdiction) onSelectAuthorizedJurisdiction?.(jurisdiction);
              },
            }}
          />
          <small className="d-block mt-1">{authorizedStatus}</small>
        </Form.Group>

        {unauthorizedNote ? (
          <small className="d-block text-warning" role="alert">
            {unauthorizedNote}
          </small>
        ) : null}
        {missingChannels.length ? (
          <small className="d-block text-warning">
            SAME {currentSameCode} is not authorized on {formatChannelNames(missingChannels)} for
            this COG. FEMA refuses it on that channel (419/519/619).
          </small>
        ) : null}

        {draft.resolvedJurisdiction ? (
          <>
            <div className="d-flex align-items-center flex-wrap gap-2">
              <span>
                {draft.resolvedJurisdiction.name} · SAME {draft.geocodes?.[0]?.value}
              </span>
              <Button
                type="button"
                size="sm"
                variant="outline-secondary"
                className="rounded-3"
                onClick={onClearJurisdiction}
              >
                Clear county
              </Button>
            </div>
            <h3 className="h6 mb-1 mt-2">Area Selection</h3>
            <p className="small mb-1">
              This alert is currently configured for the full authorized area shown above. The
              boundary is displayed on the map to help you confirm the permitted alerting area.
            </p>
            <p className="small mb-0">
              To target a smaller area, draw one or more shapes within the authorized boundary. Any
              drawn shape must remain completely inside the authorized alerting area. If no shapes
              are added, the alert will apply to the full selected area.
            </p>
          </>
        ) : (
          <span>Search for and select a county above.</span>
        )}
        <IpawsFieldFindings fieldPath="geocodes" findings={findings} />
      </div>

      <div
        className="alert-creator-polygon-summary border rounded-3 p-3"
        role="group"
        aria-labelledby="ipaws-alert-area-shapes-label"
        aria-invalid={areaFindings.length > 0}
        aria-describedby={areaFindings.length ? getIpawsFindingElementId('areaShapes') : undefined}
        aria-live="polite"
      >
        <div className="d-flex justify-content-between gap-2">
          <strong id="ipaws-alert-area-shapes-label">Alert shapes</strong>
          <span>
            {shapeCount} / {IPAWS_AREA_LIMITS.maximumShapeCount} shapes · {pointCount} /{' '}
            {IPAWS_AREA_LIMITS.maximumPointCount} polygon points
          </span>
        </div>
        {areaShapes.length ? (
          <ol className="small mb-0 mt-2 ps-3">
            {areaShapes.map((shape, index) => (
              <li key={shape.id} className="text-break">
                {getIpawsAreaShapeLabel(shape, index)}: {getShapeDescription(shape)}
              </li>
            ))}
          </ol>
        ) : hasIpawsSameGeocode(draft) ? (
          <small className="d-block mt-2">No map shapes: the whole targeted area is alerted.</small>
        ) : null}
        {/* IDG "NWR (NWEM) Polygon & Circle Processing": NWS does not accept circles. FEMA
            publishes no error code for it, so this is a note, never a refusal. */}
        {hasCircle && isNwemSelected ? (
          <small className="d-block mt-2 text-warning" role="note">
            ⚠️ NWEM/NOAA Weather Radio does not use circles. IPAWS will default to the selected
            authorized area(s) for NWEM/NOAA Weather Radio.
            {isWeaSelected ? ' Circle(s) will still be used for WEA targeting.' : ''}
          </small>
        ) : null}
        {areaFindings.length ? (
          <ul id={getIpawsFindingElementId('areaShapes')} className="mb-0 mt-2 ps-3">
            {areaFindings.map((finding) => (
              <li key={finding}>{finding}</li>
            ))}
          </ul>
        ) : (
          <small className="d-block mt-1">All shapes pass IPAWS shape checks.</small>
        )}
      </div>

      <Button
        type="button"
        size="sm"
        variant="secondary"
        className="alert-creator-review-action align-self-start rounded-3 fw-semibold"
        onClick={onReviewDraft}
      >
        Check required fields
      </Button>
      {hasReviewedDraft && (
        <small className="d-block" role="status" aria-live="polite">
          {validationIssues?.length
            ? `${validationIssues.length} IPAWS issue${
                validationIssues.length === 1 ? '' : 's'
              } remain. Review the THEN validation list above.`
            : findings.length
              ? `${findings.length} frontend finding${
                  findings.length === 1 ? '' : 's'
                } remain. Review the fields above.`
              : 'Frontend checks complete. The message still requires server validation.'}
        </small>
      )}
    </fieldset>
  );
};

export default IpawsAffectedAreaSection;
